import { useState, useEffect, useCallback, useMemo } from 'react'
import { api } from '../api/client'
import { useWorkspaceContext } from '../hooks/useWorkspaceContext'
import Tabs from '../components/Tabs'

interface Competitor { competitorID: number; platformName?: string; handle: string; displayName?: string; createdAt: string }
interface CompetitorSnapshot { snapshotDate: string; followerCount: number; postCount?: number; avgEngagementRate?: number; topPostUrl?: string }
interface Report { reportID: number; name: string; reportTypeID: number; brandID?: number; scheduleFrequency?: string; createdAt: string }
interface TrendLine { competitorID: number; handle: string; displayName?: string; dataPoints: { date: string; followers: number; posts?: number; engagement?: number }[] }

const TABS = [
  { id: 'competitors', label: 'Competitors' },
  { id: 'graphs', label: 'Analytics' },
  { id: 'trends', label: 'Trends' },
  { id: 'reports', label: 'Reports' },
  { id: 'export', label: 'Export' },
]
const COLORS = ['#1b6ec2', '#27ae60', '#e74c3c', '#f39c12', '#8e44ad', '#2c3e50', '#e67e22', '#1abc9c']

export default function Competitors() {
  const { activeWorkspace } = useWorkspaceContext()
  const wsId = activeWorkspace?.workspaceID ?? 0
  const [tab, setTab] = useState('competitors')
  const [competitors, setCompetitors] = useState<Competitor[]>([])
  const [snapshots, setSnapshots] = useState<Record<number, CompetitorSnapshot[]>>({})
  const [selectedComp, setSelectedComp] = useState<number | null>(null)
  const [reports, setReports] = useState<Report[]>([])
  const [msg, setMsg] = useState('')
  const [newHandle, setNewHandle] = useState('')
  const [newPlatform, setNewPlatform] = useState(1)
  const [trends, setTrends] = useState<TrendLine[]>([])
  const [trendDays, setTrendDays] = useState(90)
  const [exportBrandName, setExportBrandName] = useState('')
  const [exportData, setExportData] = useState<Record<string, unknown> | null>(null)

  useEffect(() => { loadCompetitors(); loadReports() }, [])

  useEffect(() => {
    api.get<TrendLine[]>(`/workspaces/${wsId}/competitors/trend?days=${trendDays}`)
      .then(setTrends).catch(() => setTrends([]))
  }, [trendDays, competitors.length])

  const loadCompetitors = async () => {
    try { setCompetitors(await api.get<Competitor[]>(`/workspaces/${wsId}/competitors`)) } catch (err) { console.error('[SMM]', err) }
  }
  const loadReports = async () => {
    try { setReports(await api.get<Report[]>(`/workspaces/${wsId}/reports`)) } catch (err) { console.error('[SMM]', err) }
  }

  const selectCompetitor = useCallback(async (id: number) => {
    setSelectedComp(id)
    if (snapshots[id]) return
    try {
      const s = await api.get<CompetitorSnapshot[]>(`/workspaces/${wsId}/competitors/${id}/snapshots?days=90`)
      setSnapshots(prev => ({ ...prev, [id]: s }))
    } catch (err) { console.error('[SMM]', err); setSnapshots(prev => ({ ...prev, [id]: [] })) }
  }, [snapshots])

  const handleAddCompetitor = async () => {
    if (!newHandle.trim()) return
    try { await api.post(`/workspaces/${wsId}/competitors`, { platformID: newPlatform, handle: newHandle }); setNewHandle(''); setMsg('Competitor added'); loadCompetitors() } catch (err) { console.error('[SMM]', err); setMsg('Failed') }
  }

  const handleDeleteComp = async (id: number) => {
    try { await api.del(`/workspaces/${wsId}/competitors/${id}`); loadCompetitors(); setMsg('Removed') } catch (err) { console.error('[SMM]', err); setMsg('Failed') }
  }

  const latestSnapshots = useMemo(() => {
    return competitors.map(c => {
      const snaps = snapshots[c.competitorID]
      const latest = snaps && snaps.length > 0 ? snaps[snaps.length - 1] : null
      return { ...c, latest }
    })
  }, [competitors, snapshots])

  useEffect(() => {
    competitors.forEach(c => {
      if (!snapshots[c.competitorID]) {
        api.get<CompetitorSnapshot[]>(`/workspaces/${wsId}/competitors/${c.competitorID}/snapshots?days=90`)
          .then(s => setSnapshots(prev => ({ ...prev, [c.competitorID]: s })))
          .catch(() => setSnapshots(prev => ({ ...prev, [c.competitorID]: [] })))
      }
    })
  }, [competitors])

  const maxFollowers = useMemo(() => Math.max(...latestSnapshots.map(s => s.latest?.followerCount ?? 0), 1), [latestSnapshots])

  const selectedSnaps = selectedComp ? snapshots[selectedComp] || [] : []

  return (
    <div className="smm-competitors">
      <h1>Competitor Benchmarking</h1>
      {msg && <div className="smm-success-card">{msg}</div>}
      <Tabs tabs={TABS} activeTab={tab} onTabChange={setTab} />

      {tab === 'competitors' && (
        <>
          <div className="smm-card">
            <h3>Tracked Competitors</h3>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <select className="smm-select" value={newPlatform} onChange={e => setNewPlatform(Number(e.target.value))}>
                <option value={1}>Instagram</option><option value={2}>Facebook</option><option value={3}>X/Twitter</option>
                <option value={4}>LinkedIn</option><option value={5}>TikTok</option><option value={6}>YouTube</option>
              </select>
              <input className="smm-input" style={{ flex: 1, minWidth: 200 }} placeholder="@handle" value={newHandle} onChange={e => setNewHandle(e.target.value)} />
              <button className="smm-btn smm-btn-primary" onClick={handleAddCompetitor}>Add Competitor</button>
            </div>
            <table className="smm-table">
              <thead><tr><th>Handle</th><th>Platform</th><th>Display Name</th><th>Followers</th><th>Engagement</th><th></th></tr></thead>
              <tbody>
                {latestSnapshots.map(c => (
                  <tr key={c.competitorID} onClick={() => selectCompetitor(c.competitorID)} style={{ cursor: 'pointer' }} className={selectedComp === c.competitorID ? 'smm-row-selected' : ''}>
                    <td><strong>@{c.handle}</strong></td><td>{c.platformName || '-'}</td><td>{c.displayName || '-'}</td>
                    <td>{c.latest?.followerCount?.toLocaleString() ?? '-'}</td>
                    <td>{c.latest?.avgEngagementRate ? `${(c.latest.avgEngagementRate * 100).toFixed(2)}%` : '-'}</td>
                    <td><button className="smm-btn smm-btn-sm smm-btn-danger" onClick={e => { e.stopPropagation(); handleDeleteComp(c.competitorID) }}>x</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selectedComp && selectedSnaps.length > 0 && (
            <div className="smm-card">
              <h3>Snapshots (90 days)</h3>
              <table className="smm-table"><thead><tr><th>Date</th><th>Followers</th><th>Posts</th><th>Engagement</th></tr></thead>
                <tbody>{selectedSnaps.map((s, i) => <tr key={i}><td>{new Date(s.snapshotDate).toLocaleDateString()}</td><td>{s.followerCount.toLocaleString()}</td><td>{s.postCount ?? '-'}</td><td>{s.avgEngagementRate ? `${(s.avgEngagementRate * 100).toFixed(2)}%` : '-'}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'graphs' && (
        <>
          <div className="smm-card">
            <h3>Follower Comparison</h3>
            {latestSnapshots.length > 0 ? (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {latestSnapshots.map((c, i) => (
                  <div key={c.competitorID} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: 140, fontWeight: 500, fontSize: '0.85rem' }}>@{c.handle}</div>
                    <div style={{ flex: 1, height: 28, background: '#f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${((c.latest?.followerCount ?? 0) / maxFollowers) * 100}%`, background: COLORS[i % COLORS.length], borderRadius: 8, transition: 'width 0.5s ease' }} />
                    </div>
                    <span style={{ fontWeight: 600, minWidth: 90, textAlign: 'right' }}>{(c.latest?.followerCount ?? 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : <p className="smm-hint">Add competitors to see comparison charts.</p>}
          </div>
          <div className="smm-card">
            <h3>Engagement Rate Comparison</h3>
            {latestSnapshots.filter(c => c.latest?.avgEngagementRate).length > 0 ? (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {latestSnapshots.filter(c => c.latest?.avgEngagementRate).map((c, i) => {
                  const rate = (c.latest?.avgEngagementRate ?? 0) * 100
                  return (
                    <div key={c.competitorID} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: 140, fontWeight: 500, fontSize: '0.85rem' }}>@{c.handle}</div>
                      <div style={{ flex: 1, height: 28, background: '#f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(rate * 10, 100)}%`, background: COLORS[i % COLORS.length], borderRadius: 8 }} />
                      </div>
                      <span style={{ fontWeight: 600, minWidth: 70, textAlign: 'right' }}>{rate.toFixed(2)}%</span>
                    </div>
                  )
                })}
              </div>
            ) : <p className="smm-hint">Engagement data will appear after snapshots are collected.</p>}
          </div>
        </>
      )}

      {tab === 'trends' && (
        <>
          <div className="smm-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Follower Growth Trends</h3>
              <select className="smm-select" style={{ width: 'auto' }} value={trendDays} onChange={e => setTrendDays(Number(e.target.value))}>
                <option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option><option value={180}>180 days</option>
              </select>
            </div>
            {trends.length > 0 ? (
              <div>
                {trends.map((t, i) => {
                  const points = t.dataPoints
                  if (points.length < 2) return (
                    <div key={t.competitorID} style={{ marginBottom: '1rem' }}>
                      <strong style={{ color: COLORS[i % COLORS.length] }}>@{t.handle}</strong>
                      <span className="smm-hint"> -- not enough data for trend</span>
                    </div>
                  )
                  const maxF = Math.max(...points.map(p => p.followers), 1)
                  const minF = Math.min(...points.map(p => p.followers))
                  const range = maxF - minF || 1
                  const chartW = 600
                  const chartH = 80
                  const pathPoints = points.map((p, j) => {
                    const x = (j / (points.length - 1)) * chartW
                    const y = chartH - ((p.followers - minF) / range) * (chartH - 8)
                    return `${j === 0 ? 'M' : 'L'}${x},${y}`
                  }).join(' ')
                  const first = points[0]
                  const last = points[points.length - 1]
                  const growth = last.followers - first.followers
                  return (
                    <div key={t.competitorID} style={{ marginBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                        <strong style={{ color: COLORS[i % COLORS.length] }}>@{t.handle} {t.displayName ? `(${t.displayName})` : ''}</strong>
                        <span style={{ color: growth >= 0 ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
                          {growth >= 0 ? '+' : ''}{growth.toLocaleString()} ({((growth / (first.followers || 1)) * 100).toFixed(1)}%)
                        </span>
                      </div>
                      <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', maxWidth: chartW, height: chartH, border: '1px solid var(--smm-border, #e0e0e0)', borderRadius: 8, background: '#fafafa' }}>
                        <path d={pathPoints} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="2" />
                      </svg>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#999' }}>
                        <span>{new Date(first.date).toLocaleDateString()}</span>
                        <span>{first.followers.toLocaleString()} &rarr; {last.followers.toLocaleString()}</span>
                        <span>{new Date(last.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : <p className="smm-hint">Add competitors and wait for snapshots to see trend lines.</p>}
          </div>

          <div className="smm-card">
            <h3>Engagement Rate Trends</h3>
            {trends.filter(t => t.dataPoints.some(p => p.engagement)).length > 0 ? (
              <div>
                {trends.filter(t => t.dataPoints.some(p => p.engagement)).map((t, i) => {
                  const points = t.dataPoints.filter(p => p.engagement != null)
                  if (points.length < 2) return null
                  const maxE = Math.max(...points.map(p => (p.engagement ?? 0) * 100), 0.01)
                  const chartW = 600; const chartH = 60
                  const pathPoints = points.map((p, j) => {
                    const x = (j / (points.length - 1)) * chartW
                    const y = chartH - ((p.engagement ?? 0) * 100 / maxE) * (chartH - 8)
                    return `${j === 0 ? 'M' : 'L'}${x},${y}`
                  }).join(' ')
                  return (
                    <div key={t.competitorID} style={{ marginBottom: '1rem' }}>
                      <strong style={{ color: COLORS[i % COLORS.length] }}>@{t.handle}</strong>
                      <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', maxWidth: chartW, height: chartH, background: '#fafafa', borderRadius: 8, border: '1px solid var(--smm-border, #e0e0e0)' }}>
                        <path d={pathPoints} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="2" strokeDasharray="4,2" />
                      </svg>
                    </div>
                  )
                })}
              </div>
            ) : <p className="smm-hint">Engagement data will appear after snapshots are collected.</p>}
          </div>
        </>
      )}

      {tab === 'reports' && (
        <div className="smm-card">
          <h3>Reports</h3>
          {reports.length > 0 ? (
            <table className="smm-table"><thead><tr><th>Name</th><th>Type</th><th>Schedule</th><th>Created</th></tr></thead>
              <tbody>{reports.map(r => <tr key={r.reportID}><td>{r.name}</td><td>Type {r.reportTypeID}</td><td>{r.scheduleFrequency || 'Manual'}</td><td>{new Date(r.createdAt).toLocaleDateString()}</td></tr>)}</tbody>
            </table>
          ) : <p className="smm-hint">No reports configured yet. Reports can be scheduled to run automatically or generated on demand.</p>}
        </div>
      )}

      {tab === 'export' && (
        <div className="smm-card">
          <h3>White-Label Export</h3>
          <p className="smm-hint">Generate a branded competitor benchmarking report. The report omits your platform branding so you can share it as your own.</p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <input className="smm-input" style={{ minWidth: 200 }} value={exportBrandName} onChange={e => setExportBrandName(e.target.value)} placeholder="Your Brand Name" />
            <select className="smm-select" style={{ width: 'auto' }} value={trendDays} onChange={e => setTrendDays(Number(e.target.value))}>
              <option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option>
            </select>
            <button className="smm-btn smm-btn-primary" onClick={async () => {
              try {
                const data = await api.get<Record<string, unknown>>(`/workspaces/${wsId}/competitors/export?days=${trendDays}&brandName=${encodeURIComponent(exportBrandName || 'Your Brand')}`)
                setExportData(data)
              } catch (err) { console.error('[SMM]', err); setMsg('Export failed') }
            }}>Generate Report</button>
          </div>
          {exportData && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4>{(exportData.title as string) || 'Report'}</h4>
                <button className="smm-btn smm-btn-sm" onClick={() => {
                  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a'); a.href = url; a.download = `competitor-report-${trendDays}d.json`; a.click()
                  URL.revokeObjectURL(url)
                }}>Download JSON</button>
              </div>
              <pre style={{ background: '#f8f9fa', padding: '1rem', borderRadius: 8, overflow: 'auto', maxHeight: 400, fontSize: '0.8rem' }}>
                {JSON.stringify(exportData, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
